import React, { useState } from 'react';
import { FaArrowLeft, FaBuilding, FaCheckCircle, FaClock, FaGasPump, FaLocationArrow, FaMapMarkerAlt } from 'react-icons/fa';
import { OpeningHour } from '../types';
import { formatFuelType } from '../utils/formatters';
import { formatOpeningHours } from '../utils/openingHours';

export interface PreviewPrice {
  tipoCombustivel: string;
  preco: number;
}

interface StationPhonePreviewProps {
  nomePosto: string;
  bandeira?: string;
  logradouro?: string;
  numero?: string;
  complemento?: string;
  bairro?: string;
  cidade?: string;
  estado?: string;
  photoUrl?: string | null;
  aberto24h?: boolean;
  horarios?: OpeningHour[];
  precos: PreviewPrice[];
}

// Mesmas regras de exibição do app (src/utils/formatters.ts no abastecemais-app)
const LOWER_WORDS = ['de', 'da', 'do', 'das', 'dos', 'e'];
const toTitleCase = (value?: string) =>
  (value || '')
    .toLowerCase()
    .split(/\s+/)
    .map((word, i) => (i > 0 && LOWER_WORDS.includes(word) ? word : word.charAt(0).toUpperCase() + word.slice(1)))
    .join(' ');

const BRANDS: Record<string, string> = {
  BRANCA: 'Bandeira branca',
  VIBRA: 'Petrobras (Vibra)',
  RAIZEN: 'Shell (Raízen)',
  IPIRANGA: 'Ipiranga',
  ALE: 'ALE',
};
const brandName = (brand?: string) =>
  !brand ? 'Sem bandeira' : BRANDS[brand] || brand.charAt(0) + brand.slice(1).toLowerCase();

const formatPrice = (value: number) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: Math.round(value * 1000) % 10 === 0 ? 2 : 3,
  }).format(value);

const PREFERRED_FUEL = 'GASOLINA_COMUM';

/**
 * Celular simulado com o card da lista e o detalhe do posto, como aparecem no app.
 */
const StationPhonePreview: React.FC<StationPhonePreviewProps> = (props) => {
  const [screen, setScreen] = useState<'lista' | 'detalhe'>('lista');

  return (
    <div className="flex flex-col items-center w-full">
      <div className="inline-flex p-1 mb-4 rounded-xl bg-[#1a2332] border border-white/10 text-sm">
        {(['lista', 'detalhe'] as const).map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setScreen(s)}
            className={`px-4 py-1.5 rounded-lg transition-colors ${
              screen === s ? 'bg-[#089453] text-white' : 'text-white/60 hover:text-white'
            }`}
          >
            {s === 'lista' ? 'Lista de postos' : 'Detalhe do posto'}
          </button>
        ))}
      </div>

      <div className="relative w-full max-w-[390px] h-[780px] rounded-[48px] bg-[#05080b] p-3 shadow-2xl shadow-black/60 ring-1 ring-white/10">
        <div className="absolute top-5 left-1/2 -translate-x-1/2 w-24 h-6 rounded-full bg-[#05080b] z-10" />
        <div className="w-full h-full rounded-[38px] overflow-hidden bg-[#f8f9fa] flex flex-col font-sans">
          <div className="flex justify-between items-center px-7 pt-3 pb-2 text-[13px] font-semibold text-[#1a202c]">
            <span>9:41</span>
            <span className="tracking-widest text-[10px]">●●● ▮</span>
          </div>
          <div className="flex-1 overflow-y-auto">
            {screen === 'lista' ? (
              <ListScreen {...props} onOpen={() => setScreen('detalhe')} />
            ) : (
              <DetailScreen {...props} onBack={() => setScreen('lista')} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const ListScreen: React.FC<StationPhonePreviewProps & { onOpen: () => void }> = (props) => {
  const price =
    props.precos.find((p) => p.tipoCombustivel === PREFERRED_FUEL) || props.precos[0] || null;
  const fuelLabel = formatFuelType(price?.tipoCombustivel || PREFERRED_FUEL);
  const address = [
    [props.logradouro, props.numero].filter(Boolean).join(', '),
    props.bairro,
  ]
    .filter(Boolean)
    .join(' - ');

  return (
    <div className="px-4 pb-6">
      <h3 className="text-[22px] font-extrabold text-[#1a202c] mt-1">Postos perto de você</h3>
      <div className="flex gap-2 mt-3 mb-4">
        <span className="px-3 py-1 rounded-full bg-[#089453] text-white text-xs font-semibold">{fuelLabel}</span>
        <span className="px-3 py-1 rounded-full bg-white text-[#4a5568] text-xs border border-black/5">Mais perto</span>
      </div>

      <button
        type="button"
        onClick={props.onOpen}
        className="w-full text-left flex items-center bg-white rounded-xl p-4 mb-2 min-h-[88px] shadow-sm ring-2 ring-[#089453]/60"
      >
        <div className="w-16 h-16 rounded-lg bg-[#f8f9fa] flex items-center justify-center overflow-hidden mr-4 shrink-0">
          {props.photoUrl ? (
            <img src={props.photoUrl} alt="" className="w-full h-full object-cover" />
          ) : (
            <FaBuilding className="text-[#718096]" size={22} />
          )}
        </div>
        <div className="flex-1 min-w-0 mr-3">
          <p className="text-[16px] font-bold text-[#1a202c] truncate">
            {toTitleCase(props.nomePosto) || 'Nome do posto'}
          </p>
          <p className="text-[14px] text-[#4a5568] truncate">
            {brandName(props.bandeira)}
            {price && (
              <span className="text-[12px] text-[#089453] font-semibold">
                {'  '}
                <FaCheckCircle className="inline -mt-0.5" size={11} /> Preço do posto
              </span>
            )}
          </p>
          {address && <p className="text-[14px] text-[#718096] truncate">{toTitleCase(address)}</p>}
          <p className="text-[12px] text-[#4a5568] mt-1 flex items-center gap-1">
            <FaLocationArrow size={10} /> 1.2km
          </p>
        </div>
        <div className="text-right min-w-[80px]">
          {price ? (
            <>
              <p className="text-[20px] font-extrabold text-[#089453] leading-tight">{formatPrice(price.preco)}</p>
              <p className="text-[12px] text-[#718096]">por litro</p>
            </>
          ) : (
            <p className="text-[14px] text-[#718096] italic">Sem preço</p>
          )}
        </div>
      </button>
      <p className="text-[11px] text-center text-[#089453] font-semibold mb-3">↑ Seu posto</p>

      {[0, 1].map((i) => (
        <div key={i} className="flex items-center bg-white rounded-xl p-4 mb-2 min-h-[88px] shadow-sm opacity-50">
          <div className="w-16 h-16 rounded-lg bg-[#edf0f3] mr-4 shrink-0" />
          <div className="flex-1 space-y-2">
            <div className="h-3 w-3/4 rounded bg-[#e2e8f0]" />
            <div className="h-2.5 w-1/2 rounded bg-[#edf0f3]" />
            <div className="h-2.5 w-2/3 rounded bg-[#edf0f3]" />
          </div>
          <div className="h-5 w-14 rounded bg-[#e2e8f0] ml-3" />
        </div>
      ))}
    </div>
  );
};

const DetailScreen: React.FC<StationPhonePreviewProps & { onBack: () => void }> = (props) => {
  const fullAddress = [
    [props.logradouro, props.numero].filter(Boolean).join(', ') + (props.complemento ? ` - ${props.complemento}` : ''),
    props.bairro,
    [props.cidade, props.estado].filter(Boolean).join(' - '),
  ]
    .filter((part) => part && part.trim())
    .join(', ');
  const hours = formatOpeningHours(props.aberto24h, props.horarios);

  return (
    <div className="px-4 pb-6">
      <button type="button" onClick={props.onBack} className="p-2 -ml-2 text-[#1a202c]" aria-label="Voltar">
        <FaArrowLeft size={18} />
      </button>
      {props.photoUrl && (
        <img src={props.photoUrl} alt="" className="w-full aspect-video object-cover rounded-xl bg-white mb-4" />
      )}
      <h3 className="text-[24px] font-extrabold text-[#1a202c] leading-tight">
        {toTitleCase(props.nomePosto) || 'Nome do posto'}
      </h3>
      <p className="text-[16px] text-[#4a5568] mt-0.5">{brandName(props.bandeira)}</p>
      {fullAddress && (
        <p className="text-[14px] text-[#4a5568] mt-2 flex gap-1.5">
          <FaMapMarkerAlt className="mt-1 shrink-0" size={11} />
          {toTitleCase(fullAddress)}
        </p>
      )}
      {hours && (
        <p className="text-[13px] text-[#4a5568] mt-2 flex gap-1.5">
          <FaClock className="mt-0.5 shrink-0" size={11} />
          {hours}
        </p>
      )}

      <div className="bg-white rounded-xl p-4 mt-4 shadow-sm">
        <p className="text-[16px] font-bold text-[#1a202c] mb-2">Preços</p>
        {props.precos.length === 0 ? (
          <p className="text-[14px] text-[#718096] italic">Nenhum preço informado ainda</p>
        ) : (
          props.precos.map((p) => (
            <div key={p.tipoCombustivel} className="flex justify-between items-center py-2 border-t border-black/5 first:border-t-0">
              <span className="text-[14px] text-[#1a202c] flex items-center gap-2">
                <FaGasPump className="text-[#089453]" size={12} />
                {formatFuelType(p.tipoCombustivel)}
              </span>
              <span className="text-[16px] font-bold text-[#1a202c]">{formatPrice(p.preco)}</span>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default StationPhonePreview;
